import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { useImageNaturalSize } from "@/hooks/use-image-natural-size"
import { useTranslation } from "@/hooks/use-translation"
import { copyImageToClipboard, downloadImage, filenameFromUrl } from "@/lib/download-image"
import {
  buildFigureInputsFromCharacters,
  CANVAS_WIDTH,
  computeLayout,
  DEFAULT_SCALE_POSE_HEIGHT,
  DEFAULT_SCALE_POSE_WIDTH,
  parseScaleSheetEntityKey,
  scaleSheetEntityKey,
  type ScaleSheetEntityType,
  type ScaleSheetFigureInput,
} from "@/lib/scale-sheet-layout"
import type { ScaleSheetCharacterRow } from "@/lib/scale-sheet-api"

type PanelMode = "view" | "edit"

export type EntityScaleSheetRow = ScaleSheetCharacterRow & {
  entity_type?: ScaleSheetEntityType
}

export type EntityScaleSheetLayoutMeta = {
  figures?: {
    character_id?: number
    entity_id?: number
    entity_type?: string
    image_width?: number
    image_height?: number
  }[]
  content_width_preview?: number
  characters?: { id: number; image_width?: number; image_height?: number }[]
  props?: { id: number; image_width?: number; image_height?: number }[]
} | null

export type EntityScaleSheetLoadResult = {
  status: string
  idsKey: string
  image_url: string | null
  image_download_url?: string | null
  layout_meta: EntityScaleSheetLayoutMeta
  rows: EntityScaleSheetRow[]
}

export type EntityScaleSheetModalLabels = {
  close: string
  title: string
  previewTitle: string
  hint: string
  needTwo: string
  applyAll: string
  heightLabel?: string
}

export type EntityScaleSheetHeightUpdate = {
  id: number
  height_cm: number
  entity_type: ScaleSheetEntityType
}

export type EntityScaleSheetModalProps = {
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  entityIds?: number[]
  shotId?: number
  variant?: "project" | "shot"
  closeOnApply?: boolean
  onApplied?: () => void
  loadSheet: (
    projectId: number,
    entityIds: number[] | undefined,
    options: { preview: boolean; shotId?: number; editOnly: boolean },
  ) => Promise<EntityScaleSheetLoadResult>
  patchHeights: (projectId: number, updates: EntityScaleSheetHeightUpdate[]) => Promise<void>
  labels: EntityScaleSheetModalLabels
  /** Statuses that mean figures are not ready yet (auto-enter edit on shot). */
  waitingStatuses: string[]
  exportFilenamePrefix: string
  defaultHeightCm?: number
  missingFigurePlaceholder?: "prop"
  minHeightCm?: number
  minDragHeightCm?: number
  minHeightCmForEntity?: (entityKey: string) => number
  minDragHeightCmForEntity?: (entityKey: string) => number
  /** Minimum entities required to show the edit UI (characters: 1, props: 1, mixed: 2). */
  minEntities?: number
  /** Default entity type when a row omits entity_type. */
  figureEntityType?: ScaleSheetEntityType
  /** Raise above fullscreen hosts. Defaults to true for shot variant. */
  nested?: boolean
  /** Download / copy / resolution for composed sheet. Default true; canvas passes false. */
  showExportActions?: boolean
  onRegenerateFigure?: (entityKey: string) => void | Promise<void>
  regenerateFigureTitle?: string | ((entityKey: string) => string)
  isFigureBusy?: (entityKey: string) => boolean
  /** When set with onRegenerateFigure, disable from row.can_generate_scale_pose and show this reason (characters only). */
  figureGenerateDisabledReason?: string
  figureGenerateDisabled?: (entityKey: string) => { disabled: boolean; reason?: string }
}

const PENDING_SCALE_SHEET_STATUSES = new Set(["generating", "stale", "missing"])

function rowEntityType(row: EntityScaleSheetRow, defaultType: ScaleSheetEntityType): ScaleSheetEntityType {
  return row.entity_type ?? defaultType
}

export function rowEntityKey(row: EntityScaleSheetRow, defaultType: ScaleSheetEntityType): string {
  return scaleSheetEntityKey(rowEntityType(row, defaultType), row.id)
}

function initHeights(
  rows: EntityScaleSheetRow[],
  defaultEntityType: ScaleSheetEntityType,
  defaultHeightCm?: number,
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const c of rows) {
    out[rowEntityKey(c, defaultEntityType)] = c.saved_height_cm ?? c.effective_height_cm ?? defaultHeightCm ?? 0
  }
  return out
}

function mergeHeightsFromServer(
  rows: EntityScaleSheetRow[],
  prevDraft: Record<string, number>,
  prevSaved: Record<string, number>,
  prevTouched: Set<string>,
  preserveDrafts: boolean,
  defaultEntityType: ScaleSheetEntityType,
  defaultHeightCm?: number,
) {
  const draft = initHeights(rows, defaultEntityType, defaultHeightCm)
  const saved = initHeights(rows, defaultEntityType, defaultHeightCm)
  const touched = new Set<string>()
  if (!preserveDrafts) return { draft, saved, touched }

  for (const key of prevTouched) {
    if (!(key in draft)) continue
    const draftCm = prevDraft[key]
    if (draftCm == null) continue
    draft[key] = draftCm
    if (prevSaved[key] != null) saved[key] = prevSaved[key]
    touched.add(key)
  }
  return { draft, saved, touched }
}

function mergeFigureDims(
  rows: EntityScaleSheetRow[],
  layoutMeta: EntityScaleSheetLayoutMeta,
  preferFreshDims = false,
  defaultEntityType: ScaleSheetEntityType = "character",
): EntityScaleSheetRow[] {
  const byKey = new Map<string, Partial<ScaleSheetFigureInput>>()
  for (const fig of layoutMeta?.figures ?? []) {
    if (fig.image_width == null && fig.image_height == null) continue
    const id = fig.entity_id ?? fig.character_id
    if (id == null) continue
    const type = fig.entity_type === "prop" || fig.entity_type === "character" ? fig.entity_type : defaultEntityType
    byKey.set(scaleSheetEntityKey(type, id), {
      image_width: fig.image_width,
      image_height: fig.image_height,
    })
  }
  for (const row of layoutMeta?.characters ?? []) {
    const key = scaleSheetEntityKey("character", row.id)
    const prev = byKey.get(key) ?? {}
    byKey.set(key, { ...prev, ...row })
  }
  for (const row of layoutMeta?.props ?? []) {
    const key = scaleSheetEntityKey("prop", row.id)
    const prev = byKey.get(key) ?? {}
    byKey.set(key, { ...prev, ...row })
  }
  return rows.map((c) => {
    const dims = byKey.get(rowEntityKey(c, defaultEntityType))
    const imageWidth = preferFreshDims
      ? (c.image_width ?? dims?.image_width ?? DEFAULT_SCALE_POSE_WIDTH)
      : (dims?.image_width ?? c.image_width ?? DEFAULT_SCALE_POSE_WIDTH)
    const imageHeight = preferFreshDims
      ? (c.image_height ?? dims?.image_height ?? DEFAULT_SCALE_POSE_HEIGHT)
      : (dims?.image_height ?? c.image_height ?? DEFAULT_SCALE_POSE_HEIGHT)
    return {
      ...c,
      entity_type: rowEntityType(c, defaultEntityType),
      image_width: imageWidth,
      image_height: imageHeight,
    }
  })
}

export function useEntityScaleSheetModal({
  projectId,
  open,
  onOpenChange,
  entityIds,
  shotId,
  variant = "project",
  closeOnApply = variant === "project",
  onApplied,
  loadSheet,
  patchHeights,
  labels,
  waitingStatuses,
  exportFilenamePrefix,
  defaultHeightCm,
  minEntities = 1,
  figureEntityType = "character",
  nested: nestedProp,
  onRegenerateFigure,
  regenerateFigureTitle,
  isFigureBusy,
  figureGenerateDisabledReason,
  figureGenerateDisabled: figureGenerateDisabledProp,
}: EntityScaleSheetModalProps) {
  const isShot = variant === "shot"
  const nested = nestedProp ?? isShot
  const waitingStatusSet = useMemo(() => new Set(waitingStatuses), [waitingStatuses])
  const { t } = useTranslation(["shot", "common"])
  const [panelMode, setPanelMode] = useState<PanelMode>("view")
  const isViewMode = isShot && panelMode === "view"
  const isEditLayout = !isShot || panelMode === "edit"

  const [loading, setLoading] = useState(false)
  const [applying, setApplying] = useState(false)
  const [copying, setCopying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<EntityScaleSheetLoadResult | null>(null)
  const [characters, setCharacters] = useState<EntityScaleSheetRow[]>([])
  const [draftHeights, setDraftHeights] = useState<Record<string, number>>({})
  const [savedHeights, setSavedHeights] = useState<Record<string, number>>({})
  const [touchedKeys, setTouchedKeys] = useState<Set<string>>(() => new Set())
  const [livePreview, setLivePreview] = useState(true)
  const [pendingFigureKeys, setPendingFigureKeys] = useState<Set<string>>(() => new Set())
  const draftHeightsRef = useRef(draftHeights)
  const savedHeightsRef = useRef(savedHeights)
  const touchedKeysRef = useRef(touchedKeys)
  const loadGenerationRef = useRef(0)
  const panelModeRef = useRef(panelMode)
  const entityIdsRef = useRef(entityIds)
  entityIdsRef.current = entityIds
  // Stable key so parent re-creating the same id array does not wipe height drafts.
  const entityIdsKey = entityIds?.join(",") ?? ""

  const resetModalState = useCallback(() => {
    loadGenerationRef.current += 1
    setPanelMode("view")
    setDraftHeights({})
    setSavedHeights({})
    setTouchedKeys(new Set())
    setPendingFigureKeys(new Set())
    setData(null)
    setCharacters([])
    setError(null)
    setLivePreview(true)
    setLoading(false)
    setApplying(false)
    setCopying(false)
  }, [])

  useEffect(() => {
    if (!open) resetModalState()
  }, [open, resetModalState])

  useEffect(() => {
    draftHeightsRef.current = draftHeights
  }, [draftHeights])

  useEffect(() => {
    savedHeightsRef.current = savedHeights
  }, [savedHeights])

  useEffect(() => {
    touchedKeysRef.current = touchedKeys
  }, [touchedKeys])

  useEffect(() => {
    panelModeRef.current = panelMode
  }, [panelMode])

  const fetchPreview = isViewMode
  const needsServerSheet = isShot
  const isWaitingFigures = data?.status != null && waitingStatusSet.has(data.status)

  const load = useCallback(
    async (options?: { preserveDrafts?: boolean }) => {
      const currentEntityIds = entityIdsRef.current
      if (isViewMode && !shotId && currentEntityIds != null && currentEntityIds.length === 0) {
        setData(null)
        setCharacters([])
        setError(null)
        setLoading(false)
        return
      }

      const gen = ++loadGenerationRef.current
      setLoading(true)
      setError(null)
      try {
        const res = await loadSheet(projectId, currentEntityIds, {
          preview: fetchPreview,
          shotId,
          editOnly: !isShot,
        })
        if (gen !== loadGenerationRef.current) return

        const preferFreshDims = res.status != null && PENDING_SCALE_SHEET_STATUSES.has(res.status)
        const merged = mergeFigureDims(res.rows, res.layout_meta, preferFreshDims, figureEntityType)
        const preserveDrafts = options?.preserveDrafts ?? false
        const { draft, saved, touched } = mergeHeightsFromServer(
          merged,
          draftHeightsRef.current,
          savedHeightsRef.current,
          touchedKeysRef.current,
          preserveDrafts,
          figureEntityType,
          defaultHeightCm,
        )
        setData(res)
        setCharacters(merged)
        setDraftHeights(draft)
        setSavedHeights(saved)
        setTouchedKeys(touched)
        if (!preserveDrafts) {
          setLivePreview(isShot ? panelModeRef.current === "edit" : true)
        }
      } catch (e) {
        if (gen !== loadGenerationRef.current) return
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (gen === loadGenerationRef.current) setLoading(false)
      }
    },
    // entityIdsKey: same ids, new array identity must not reset drafts (storyboard refresh).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, entityIdsKey, fetchPreview, shotId, isViewMode, isShot, loadSheet, defaultHeightCm, figureEntityType],
  )

  useEffect(() => {
    if (!open) return
    void load()
  }, [open, load])

  const dirtyCount = useMemo(() => {
    let count = 0
    for (const key of touchedKeys) {
      const draft = draftHeights[key]
      if (draft == null) continue
      if (draft !== savedHeights[key]) count += 1
    }
    return count
  }, [touchedKeys, draftHeights, savedHeights])

  const shouldPoll =
    open &&
    (isWaitingFigures ||
      (needsServerSheet && data?.status != null && PENDING_SCALE_SHEET_STATUSES.has(data.status) && dirtyCount === 0))

  useEffect(() => {
    if (!shouldPoll) return
    const id = window.setInterval(() => {
      void load({ preserveDrafts: true })
    }, 3000)
    return () => window.clearInterval(id)
  }, [shouldPoll, load])

  const anyFigureBusy = Boolean(isFigureBusy && characters.some((c) => isFigureBusy(rowEntityKey(c, figureEntityType))))
  const prevAnyFigureBusyRef = useRef(false)

  useEffect(() => {
    if (!open) {
      prevAnyFigureBusyRef.current = false
      return
    }
    if (prevAnyFigureBusyRef.current && !anyFigureBusy) {
      void load({ preserveDrafts: true })
    }
    prevAnyFigureBusyRef.current = anyFigureBusy
  }, [open, anyFigureBusy, load])

  const layout = useMemo(() => {
    const inputs = buildFigureInputsFromCharacters(characters, draftHeights, figureEntityType)
    return computeLayout(inputs, "preview")
  }, [characters, draftHeights, figureEntityType])

  const previewFigures = useMemo(() => {
    const inputs = buildFigureInputsFromCharacters(characters, draftHeights, figureEntityType)
    return computeLayout(inputs, "preview").figures
  }, [characters, draftHeights, figureEntityType])

  const markHeightTouchedByKey = useCallback((entityKey: string, cm: number) => {
    setLivePreview(true)
    setTouchedKeys((prev) => {
      if (prev.has(entityKey)) return prev
      const next = new Set(prev)
      next.add(entityKey)
      return next
    })
    setDraftHeights((prev) => ({ ...prev, [entityKey]: cm }))
  }, [])

  const heightCmForEntity = useCallback(
    (entityKey: string) => {
      if (draftHeights[entityKey] != null) return draftHeights[entityKey]
      const parsed = parseScaleSheetEntityKey(entityKey)
      if (parsed == null) return defaultHeightCm ?? 0
      const row = characters.find(
        (c) => c.id === parsed.id && rowEntityType(c, figureEntityType) === parsed.entity_type,
      )
      return row?.effective_height_cm ?? defaultHeightCm ?? 0
    },
    [draftHeights, characters, defaultHeightCm, figureEntityType],
  )

  const figureGenerateDisabled = useCallback(
    (entityKey: string) => {
      if (figureGenerateDisabledProp) return figureGenerateDisabledProp(entityKey)
      const parsed = parseScaleSheetEntityKey(entityKey)
      if (parsed == null) return { disabled: true as const }
      // Front-view regenerate has no sheet prerequisite; only A-pose uses can_generate_scale_pose.
      if (parsed.entity_type === "prop") return { disabled: false as const }
      if (!onRegenerateFigure || figureGenerateDisabledReason == null) {
        return { disabled: false as const }
      }
      const row = characters.find(
        (c) => c.id === parsed.id && rowEntityType(c, figureEntityType) === parsed.entity_type,
      )
      const allowed = Boolean(row?.can_generate_scale_pose)
      return {
        disabled: !allowed,
        reason: !allowed ? figureGenerateDisabledReason : undefined,
      }
    },
    [figureGenerateDisabledProp, onRegenerateFigure, figureGenerateDisabledReason, characters, figureEntityType],
  )

  const isFigureBusyByKey = useCallback(
    (entityKey: string) => {
      if (pendingFigureKeys.has(entityKey)) return true
      if (!isFigureBusy) return false
      return isFigureBusy(entityKey)
    },
    [isFigureBusy, pendingFigureKeys],
  )

  const regenerateFigureByKey = useCallback(
    (entityKey: string) => {
      if (!onRegenerateFigure) return
      if (pendingFigureKeys.has(entityKey) || isFigureBusy?.(entityKey)) return

      setPendingFigureKeys((prev) => {
        if (prev.has(entityKey)) return prev
        const next = new Set(prev)
        next.add(entityKey)
        return next
      })

      void (async () => {
        try {
          await onRegenerateFigure(entityKey)
        } catch {
          setPendingFigureKeys((prev) => {
            if (!prev.has(entityKey)) return prev
            const next = new Set(prev)
            next.delete(entityKey)
            return next
          })
        }
      })()
    },
    [onRegenerateFigure, pendingFigureKeys, isFigureBusy],
  )

  useEffect(() => {
    if (pendingFigureKeys.size === 0 || !isFigureBusy) return
    const takenOver: string[] = []
    for (const key of pendingFigureKeys) {
      if (isFigureBusy(key)) takenOver.push(key)
    }
    if (takenOver.length === 0) return
    setPendingFigureKeys((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const key of takenOver) {
        if (next.delete(key)) changed = true
      }
      return changed ? next : prev
    })
  }, [pendingFigureKeys, isFigureBusy])

  const handleClose = useCallback(
    (next: boolean) => {
      onOpenChange(next)
    },
    [onOpenChange],
  )

  const handleApplyAll = useCallback(async () => {
    const updates = [...touchedKeys]
      .filter((key) => {
        const draft = draftHeights[key]
        if (draft == null) return false
        return draft !== savedHeights[key]
      })
      .flatMap((key) => {
        const parsed = parseScaleSheetEntityKey(key)
        if (parsed == null) return []
        return [{ id: parsed.id, height_cm: draftHeights[key]!, entity_type: parsed.entity_type }]
      })
    if (updates.length === 0) return
    setApplying(true)
    setError(null)
    try {
      await patchHeights(projectId, updates)
      onApplied?.()
      if (closeOnApply) {
        handleClose(false)
      } else {
        setPanelMode("view")
        setLivePreview(false)
        setTouchedKeys(new Set())
        await load({ preserveDrafts: false })
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setApplying(false)
    }
  }, [touchedKeys, draftHeights, savedHeights, patchHeights, projectId, onApplied, closeOnApply, handleClose, load])

  const showLive = isEditLayout && (!isShot || livePreview || dirtyCount > 0 || data?.status !== "ready")

  const sheetComposePending = needsServerSheet && data?.status != null && PENDING_SCALE_SHEET_STATUSES.has(data.status)

  // Shot view = composed image when ready. Waiting for figures → edit with stubs + generate
  // (do not show a stale composed image — it hides missing heroes behind wrong preview).
  useEffect(() => {
    if (!open || !isShot || !data) return
    if (!waitingStatusSet.has(data.status)) return
    if (characters.length < minEntities) return
    setPanelMode("edit")
    setLivePreview(true)
  }, [open, isShot, data, waitingStatusSet, characters.length, minEntities])

  // A-pose generate on live figures (project always; shot only in edit / waiting stubs).
  const showFigureRegenerate = Boolean(onRegenerateFigure) && (!isShot || isEditLayout)

  const previewContentWidth = data?.layout_meta?.content_width_preview ?? layout.content_width_preview ?? CANVAS_WIDTH
  const previewLoading =
    applying || (needsServerSheet && isViewMode && loading && !data?.image_url) || sheetComposePending

  const enterEditMode = useCallback(() => {
    if (previewLoading || !data?.image_url) return
    setPanelMode("edit")
    setLivePreview(true)
  }, [previewLoading, data?.image_url])

  const exitEditMode = useCallback(() => {
    // Stay in edit while poses are missing — composed preview is not the source of truth.
    if (data?.status != null && waitingStatusSet.has(data.status)) return
    setDraftHeights(initHeights(characters, figureEntityType, defaultHeightCm))
    setSavedHeights(initHeights(characters, figureEntityType, defaultHeightCm))
    setTouchedKeys(new Set())
    setPanelMode("view")
    setLivePreview(false)
  }, [characters, defaultHeightCm, data?.status, waitingStatusSet, figureEntityType])

  const scaleSheetDownloadUrl = data?.image_download_url ?? null
  const canDownloadScaleSheet = Boolean(scaleSheetDownloadUrl) && !previewLoading

  const largestImageUrl = data?.image_download_url ?? data?.image_url ?? null
  const showScaleSheetResolution = Boolean(largestImageUrl && data?.image_url && !previewLoading)
  const imageNaturalSize = useImageNaturalSize(largestImageUrl, open && showScaleSheetResolution)

  const serverImageReady = !showLive && Boolean(data?.image_url)
  const serverImageOpenUrl = previewLoading ? null : (data?.image_download_url ?? data?.image_url ?? null)

  const handleScaleSheetDownload = useCallback(() => {
    if (!scaleSheetDownloadUrl) return
    void downloadImage(
      scaleSheetDownloadUrl,
      filenameFromUrl(scaleSheetDownloadUrl, `${exportFilenamePrefix}-${data?.idsKey ?? "export"}`),
    )
  }, [scaleSheetDownloadUrl, exportFilenamePrefix, data?.idsKey])

  const handleScaleSheetCopy = useCallback(async () => {
    if (!scaleSheetDownloadUrl || copying) return
    setCopying(true)
    try {
      await copyImageToClipboard(scaleSheetDownloadUrl)
      toast.success(t("common.originalCopied", { ns: "shot" }))
    } catch {
      toast.error(t("common.copyImageFailed", { ns: "shot" }))
    } finally {
      setCopying(false)
    }
  }, [scaleSheetDownloadUrl, copying, t])

  const showBackInFooter =
    isShot && !isViewMode && data?.status != null && !waitingStatusSet.has(data.status) && Boolean(data?.image_url)

  const showServerPreview =
    data?.status != null &&
    !waitingStatusSet.has(data.status) &&
    (Boolean(data?.image_url) || previewLoading || sheetComposePending)

  return {
    open,
    nested,
    isShot,
    labels,
    t,
    error,
    loading,
    data,
    characters,
    minEntities,
    isViewMode,
    dirtyCount,
    applying,
    copying,
    previewContentWidth,
    previewLoading,
    serverImageOpenUrl,
    enterEditMode,
    showServerPreview,
    previewFigures,
    layout,
    serverImageReady,
    heightCmForEntity,
    markHeightTouchedByKey,
    showFigureRegenerate,
    regenerateFigureByKey,
    regenerateFigureTitle,
    isFigureBusyByKey,
    figureGenerateDisabled,
    hasFigureBusyCallback: Boolean(isFigureBusy),
    handleClose,
    handleApplyAll,
    exitEditMode,
    showBackInFooter,
    showScaleSheetResolution,
    imageNaturalSize,
    scaleSheetDownloadUrl,
    canDownloadScaleSheet,
    handleScaleSheetDownload,
    handleScaleSheetCopy,
  }
}
